import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Filter, RotateCcw, DollarSign, FileText
} from 'lucide-react';
import { toast } from 'react-toastify';
import { accountsService } from '../../../services/accountsService';
import customerMasterService from '../../../services/customerMasterService';
import Breadcrumb from '../../../components/Common/Breadcrumb';
import DataTable from '../../../components/DataTable/DataTable';

const OutstandingPaymentsReport = () => {
  const navigate = useNavigate();
  
  const [filters, setFilters] = useState({
    customer: ''
  });
  
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [customers, setCustomers] = useState([]);

  // Load customers for dropdown
  useEffect(() => {
    const loadCustomers = async () => {
      try {
        const response = await customerMasterService.getAllCustomers({ limit: 1000 });
        if (response && response.success) {
          setCustomers(response.data || []);
        }
      } catch (error) {
        console.error('Error loading customers:', error);
      }
    };
    loadCustomers();
  }, []);

  // Load report data
  useEffect(() => {
    loadReportData();
  }, [filters.customer]);

  const loadReportData = async () => {
    setLoading(true);
    try {
      const params = {};
      
      if (filters.customer) {
        params.customer = filters.customer;
      }
      
      const response = await accountsService.getOutstandingPaymentsReport(params);

      if (response && response.success) {
        setReportData(response.data);
      } else {
        toast.error('Failed to load outstanding payments report');
      }
    } catch (error) {
      console.error('Error loading outstanding payments report:', error);
      toast.error('Failed to load outstanding payments report');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleResetFilter = () => {
    setFilters({
      customer: ''
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  const formatPercentage = (value) => {
    return new Intl.NumberFormat('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value || 0);
  };

  // Define columns for DataTable
  const columns = [
    {
      key: 'invoiceNumber',
      label: 'Invoice No.',
      sortable: true,
      render: (value) => value || '-'
    },
    {
      key: 'customerName',
      label: 'Customer Name',
      sortable: true,
      render: (value) => value || '-'
    },
    {
      key: 'gstin',
      label: 'GST #',
      sortable: true,
      render: (value) => value || '-'
    },
    {
      key: 'sowAmount',
      label: 'SOW Amount (₹)',
      sortable: true,
      render: (value) => formatCurrency(value)
    },
    {
      key: 'invoicedAmount',
      label: 'Invoiced (₹)',
      sortable: true,
      render: (value) => formatCurrency(value)
    },
    {
      key: 'paidAmount',
      label: 'Paid (₹)',
      sortable: true,
      render: (value) => formatCurrency(value)
    },
    {
      key: 'outstandingAmount',
      label: 'Outstanding (₹)',
      sortable: true,
      render: (value) => formatCurrency(value)
    },
    {
      key: 'uninvoicedAmount',
      label: 'Uninvoiced (₹)',
      sortable: true,
      render: (value) => formatCurrency(value)
    },
    {
      key: 'paidPercentage',
      label: 'Paid %',
      sortable: true,
      render: (value) => formatPercentage(value)
    },
    {
      key: 'outstandingPercentage',
      label: 'Outstanding %',
      sortable: true,
      render: (value) => formatPercentage(value)
    },
    {
      key: 'uninvoicedPercentage',
      label: 'Uninvoiced %',
      sortable: true,
      render: (value) => formatPercentage(value)
    },
    {
      key: 'actions',
      label: '',
      sortable: false,
      render: () => <span style={{ display: 'none' }}></span> // Render empty span to keep column in DOM
    }
  ]; // Keep actions column in array to prevent DataTable from auto-adding one, but hide it with CSS

  // Breadcrumb items
  const breadcrumbItems = [
    {
      label: 'Accounts',
      path: '/accounts',
      onClick: () => navigate('/accounts')
    },
    {
      label: 'Reports',
      path: '/accounts?tab=reports',
      onClick: () => navigate('/accounts?tab=reports')
    },
    {
      label: 'Outstanding Payments Report',
      path: '/accounts/reports/outstanding-payments',
      isCurrent: true
    }
  ];

  return (
    <div className="sales-dashboard">
      {/* Breadcrumb Navigation */}
      <div className="breadcrumb-section">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      {/* Page Header */}
      <div className="page-header">
        <div>
          <h1 style={{ fontSize: '22px' }}>Outstanding Payments Report</h1>
          <p style={{ fontSize: '12px' }}>Only invoices that still have an amount outstanding.</p>
        </div>
      </div>

      {/* Filters Section */}
      <div style={{
        background: 'white',
        padding: '16px 20px',
        marginBottom: '20px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        borderRadius: '0'
      }}>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr auto auto', gap: '10px', alignItems: 'end' }}>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label" style={{ marginBottom: '4px' }}>Select Customer</label>
            <select
              className="form-input"
              value={filters.customer}
              onChange={(e) => handleFilterChange('customer', e.target.value)}
              style={{ padding: '6px 10px' }}
            >
              <option value="">All Customers</option>
              {customers.map(customer => (
                <option key={customer._id || customer.id} value={customer._id || customer.id}>
                  {customer.customerName} ({customer.customerCode})
                </option>
              ))}
            </select>
          </div>

          <button
            type="button"
            onClick={loadReportData}
            className="btn btn-primary"
            style={{ 
              padding: '8px 16px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              backgroundColor: '#ec4899',
              borderColor: '#ec4899'
            }}
            disabled={loading}
          >
            <Filter size={16} />
            Apply Filter
          </button>

          <button
            type="button"
            onClick={handleResetFilter}
            className="btn btn-secondary"
            style={{ 
              padding: '8px 16px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            <RotateCcw size={16} />
            Reset Filter
          </button>
        </div>
      </div>

      {/* Data Table - Direct in parent container, no wrapper */}
      <div style={{ 
        marginBottom: '80px', 
        marginLeft: '0', 
        marginRight: '0',
        overflowX: 'hidden',
        width: '100%',
        maxWidth: '100%'
      }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <div>Loading outstanding payments report...</div>
          </div>
        ) : reportData && reportData.customers && reportData.customers.length > 0 ? (
          <DataTable
            data={reportData.customers}
            columns={columns}
            loading={loading}
            pagination={true}
            pageSize={50}
            noCard={true}
            className="outstanding-payments-report-table"
            title=""
          />
        ) : (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <DollarSign size={48} style={{ color: 'var(--color-text-muted, #8B93A7)', marginBottom: '16px' }} />
            <div style={{ color: 'var(--color-text-secondary, #5B6478)' }}>No outstanding payments found</div>
          </div>
        )}
      </div>

      {/* Summary Footer - Fixed at bottom, matching table width */}
      {reportData && reportData.summary && (
        <div style={{
          position: 'fixed',
          bottom: 0,
          left: '314px', // Sidebar (250px) + left padding (64px)
          right: '64px', // Right padding to match sales-dashboard
          background: '#E8E1F5',
          padding: '14px 20px',
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: '20px',
          boxShadow: '0 -2px 8px rgba(0,0,0,0.1)',
          zIndex: 100,
          borderRadius: '0',
          borderTop: '2px solid #E8E1F5'
        }}>
          <div>
            <div style={{ fontSize: '14px', color: '#51418F', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '4px' }}>
              <span>→</span> Total SOW Amount
            </div>
            <div style={{ fontSize: '20px', fontWeight: '600', color: '#51418F' }}>
              ₹ {formatCurrency(reportData.summary.totalSOWAmount)}
            </div>
          </div>
          <div>
            <div style={{ fontSize: '14px', color: '#51418F', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '4px' }}>
              <span>→</span> Total Invoiced
            </div>
            <div style={{ fontSize: '20px', fontWeight: '600', color: '#51418F' }}>
              ₹ {formatCurrency(reportData.summary.totalInvoiced)}
            </div>
          </div>
          <div>
            <div style={{ fontSize: '14px', color: '#51418F', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '4px' }}>
              <span>→</span> Total Paid
            </div>
            <div style={{ fontSize: '20px', fontWeight: '600', color: '#51418F' }}>
              ₹ {formatCurrency(reportData.summary.totalPaid)}
            </div>
          </div>
          <div>
            <div style={{ fontSize: '14px', color: '#51418F', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '4px' }}>
              <span>→</span> Total Outstanding
            </div>
            <div style={{ fontSize: '20px', fontWeight: '600', color: '#51418F' }}>
              ₹ {formatCurrency(reportData.summary.totalOutstanding)}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OutstandingPaymentsReport;

